import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'

interface Props {
  children: ReactNode
  sectionTitle?: string
  onReset?: () => void
}

interface State {
  hasError: boolean
  error: Error | null
}

export class SectionErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('SectionErrorBoundary caught an error:', error, errorInfo)
  }

  public handleRetry = () => {
    this.setState({ hasError: false, error: null })
    if (this.props.onReset) {
      this.props.onReset()
    }
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div
          className="card"
          style={{
            padding: '2.5rem 1.5rem',
            backgroundColor: 'rgba(217, 35, 44, 0.04)',
            border: '1px solid rgba(217, 35, 44, 0.25)',
            borderRadius: 'var(--radius-md, 8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.85rem',
            textAlign: 'center',
            margin: '1rem 0',
          }}
        >
          <AlertCircle size={36} style={{ color: 'var(--brand-red, #d9232c)' }} />
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
            Error al renderizar {this.props.sectionTitle || 'esta sección'}
          </h3>
          <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', maxWidth: '520px', margin: 0, lineHeight: 1.5 }}>
            Se ha producido un problema inesperado en la interfaz. La aplicación continúa funcionando.
            Puedes reintentar la carga o navegar a otra sección.
          </p>

          {this.state.error && (
            <div
              style={{
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                backgroundColor: '#ffffff',
                border: '1px solid #fecaca',
                borderRadius: 'var(--radius-sm, 4px)',
                padding: '0.5rem 0.75rem',
                maxWidth: '600px',
                color: '#b91c1c',
                textAlign: 'left',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message || String(this.state.error)}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={this.handleRetry}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}
            >
              <RefreshCw size={14} />
              <span>Reintentar</span>
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
